// Preferred-pharmacy data + thin save client. The selectable pharmacy list is a STATIC client-side
// seed (copied from Luan's consumer mockup) — there is no pharmacy directory/EMR lookup; "search" is a
// client-side substring filter over this list. Saving persists the chosen pharmacy + delivery
// preference via PUT /v1/patient/pharmacy (thin fetch, mirrors the avatar/insurance pattern). The saved
// value is read back on GET /v1/patient/me (preferred_pharmacy).
import { useMemo } from "react";
import { API_ORIGIN } from "../config";
import { useAppAuth } from "../auth";

export interface Pharmacy {
  id: string;
  name: string;
  address: string;
  phone: string;
  distance?: string;
  hours: string;
}

export type DeliveryPref = "pickup" | "delivery";

export interface SavedPharmacy {
  id: string;
  name: string;
  address: string;
  phone: string;
  hours: string;
  delivery_pref: DeliveryPref;
}

// Static seed (Orange-County demo pharmacies, verbatim from the mockup). Not a real directory.
export const PHARMACIES: Pharmacy[] = [
  { id: "cvs-main", name: "CVS Pharmacy", address: "200 N Main St, Santa Ana, CA 92701", phone: "+1 (714) 555-0143", distance: "0.4 mi", hours: "Open 24 hours" },
  { id: "cvs-tustin", name: "CVS Pharmacy", address: "13912 Red Hill Ave, Tustin, CA 92780", phone: "+1 (714) 555-0287", distance: "1.2 mi", hours: "Open until 10 PM" },
  { id: "walgreens-1", name: "Walgreens", address: "1200 Tustin Ave, Santa Ana, CA 92705", phone: "+1 (714) 555-0455", distance: "1.6 mi", hours: "Open until 10 PM" },
  { id: "walgreens-2", name: "Walgreens", address: "17582 17th St, Tustin, CA 92780", phone: "+1 (714) 555-0612", distance: "2.1 mi", hours: "Open until 9 PM" },
  { id: "riteaid", name: "Rite Aid", address: "2300 N Tustin Ave, Santa Ana, CA 92705", phone: "+1 (714) 555-0729", distance: "2.4 mi", hours: "Open until 9 PM" },
  { id: "cvs-target", name: "CVS at Target", address: "2475 N Tustin Ave, Santa Ana, CA 92705", phone: "+1 (714) 555-0834", distance: "2.6 mi", hours: "Open until 9 PM" },
  { id: "costco", name: "Costco Pharmacy", address: "2700 Park Ave, Tustin, CA 92782", phone: "+1 (714) 555-0951", distance: "3.0 mi", hours: "Open until 8:30 PM" },
  { id: "ralphs", name: "Ralphs Pharmacy", address: "2500 Edinger Ave, Tustin, CA 92782", phone: "+1 (714) 555-1024", distance: "3.5 mi", hours: "Open until 9 PM" },
  { id: "albertsons", name: "Albertsons Pharmacy", address: "13522 Newport Ave, Tustin, CA 92780", phone: "+1 (714) 555-1186", distance: "3.8 mi", hours: "Open until 8 PM" },
  { id: "walmart", name: "Walmart Pharmacy", address: "15555 Lake Forest Dr, Irvine, CA 92618", phone: "+1 (949) 555-1293", distance: "5.2 mi", hours: "Open until 9 PM" },
];

export function usePharmacyApi(): {
  save(p: SavedPharmacy): Promise<SavedPharmacy>;
  loadSaved(): Promise<SavedPharmacy | null>;
} {
  const { getToken } = useAppAuth();
  return useMemo(() => {
    async function authHeaders(extra?: Record<string, string>): Promise<Record<string, string>> {
      const h: Record<string, string> = { ...extra };
      const token = await getToken();
      if (token) h["Authorization"] = `Bearer ${token}`;
      return h;
    }
    return {
      async save(pharmacy: SavedPharmacy): Promise<SavedPharmacy> {
        const res = await fetch(`${API_ORIGIN}/v1/patient/pharmacy`, {
          method: "PUT",
          headers: await authHeaders({ "Content-Type": "application/json" }),
          credentials: "include",
          body: JSON.stringify(pharmacy),
        });
        const text = await res.text();
        const json: unknown = text ? JSON.parse(text) : {};
        if (!res.ok) {
          const err = (json as { error?: { message?: string } }).error;
          throw new Error(err?.message ?? "Couldn’t save your pharmacy.");
        }
        return (json as { data?: { preferred_pharmacy: SavedPharmacy } }).data?.preferred_pharmacy ?? pharmacy;
      },
      // Read the saved pharmacy from /me (the published me schema strips preferred_pharmacy).
      async loadSaved(): Promise<SavedPharmacy | null> {
        const res = await fetch(`${API_ORIGIN}/v1/patient/me`, {
          method: "GET",
          headers: await authHeaders(),
          credentials: "include",
        });
        if (!res.ok) return null;
        const json = (await res.json()) as { data?: { preferred_pharmacy?: SavedPharmacy | null } };
        return json.data?.preferred_pharmacy ?? null;
      },
    };
  }, [getToken]);
}
